import { createAsyncThunk } from '@reduxjs/toolkit';
import { CurrentUser } from '../../params/redux';
import CustomerApi from '../../apis/customer';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { resettingCustomerLoader } from '../slices/customer';
import { RootState } from '../../redux/store';
import { InviteCustomerParams, SignupCustomerParams } from '../../params/redux/customer';
import { replaceToken, settingCurrentUser } from '../slices/index';

interface SignUpResponse extends Record<string, unknown> {
    success: 1 | 0;
    msg: string;
    token: string;
    user: CurrentUser;
}

export const fetchClientsCustomerThunk = createAsyncThunk(
    'customer/fetch',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CustomerApi.fetchCustomers(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.customers;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingCustomerLoader());
        }
    }
);

export const inviteCustomerThunk = createAsyncThunk(
    'customer/invite',
    async (data: InviteCustomerParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CustomerApi.inviteCustomer(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchClientsCustomerThunk(data.client_id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingCustomerLoader());
        }
    }
);

export const editCustomerThunk = createAsyncThunk(
    'customer/edit',
    async (data: InviteCustomerParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CustomerApi.editCustomer(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchClientsCustomerThunk(data.client_id as number)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingCustomerLoader());
        }
    }
);

export const signupCustomerThunk = createAsyncThunk(
    'customer/signup',
    async (data: SignupCustomerParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: SignUpResponse = await CustomerApi.singupCustomer(state.app.token, data) as SignUpResponse;
            if (response?.success === 1) {
                dispatch(replaceToken(response.token));
                dispatch(settingCurrentUser(response.user));
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingCustomerLoader());
        }
    }
);

export const deleteCustomerThunk = createAsyncThunk(
    'customer/delete',
    async ({ customer_id, client_id }: { customer_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CustomerApi.deleteCustomer(state.app.token, customer_id);
            if (response?.success === 1) {
                await dispatch(fetchClientsCustomerThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingCustomerLoader());
        }
    }
);

export const resendCustomerInvitationThunk = createAsyncThunk(
    'customer/resend',
    async ({ email, client_id }: { email: string, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await CustomerApi.resendInvitation(state.app.token, client_id, email);
            if (response?.success === 1) {
                await dispatch(fetchClientsCustomerThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingCustomerLoader());
        }
    }
);